import {
    type App,
    computed,
    type ComputedRef,
    inject,
    type Ref,
    toRef,
} from 'vue'
import type {
    AffirmFinanceOptionsFragment,
    CatalogueEngagementRingFilterInput,
    CatalogueEngagementRingInputFragment,
    CatalogueEngagementRingSignatureStoneInput,
    CurrencyInput,
    GemsApi_StoneCategory,
    KlarnaFinanceOptionsFragment,
    MoneyFragment,
    MonolithImageSizeFragment,
    RbFragment,
    RingBuilderCentreStoneFragmentFragment,
    RingBuilderCentreStoneFragmentInput,
    RingBuilderFragmentFragment,
    RingBuilderMountFragmentFragment,
    RingBuilderSetMutation,
    RingBuilderSetMutationVariables,
    TaxSnippetFragment,
} from '@/graphql/api'
import { assertNever, type GraphQLTypeName, type ReadonlyRef } from '@/helpers'
import type { AppTranslator } from '@/i18n'
import type { RegionPlugin } from '@/plugins/region.ts'
import type { MoneyFormatter } from '@/plugins/money-formatter.ts'
import type { Routes } from '@/router/routes.ts'
import type { RouterNavigationPlugin } from '@/plugins/released-urls/useRouterNavigation.ts'
import {
    type RingBuilderCookieStore,
    useRingBuilderStore,
} from '@/plugins/ring-builder-store.ts'
import type { SimpleMutationResponse } from '@/helpers/mutation-adapter.ts'
import {
    getStoneCategory,
    getStoneColorAndClarity,
    getStoneShape,
    getStoneWeight,
    type RingBuilderCentreStoneShapeConstraint,
    type RingBuilderRingTypes,
    type RingBuilderStoneTypes,
} from '@/domain/product/ring/ring-builder.ts'
import type { NonSkuParams } from '@/views/product/engagement-ring/composables/nonSkuParams.ts'
import { toCertificate } from '@/domain/stone/certificate.ts'
import {
    gemsApiStoneToInput,
    getCatalogueEngagementRingFilterInput,
    nonSkuLabGemstoneToFilterInput,
    nonSkuLabGrownDiamondToFilterInput,
} from '@/domain/product/ring/engagement.ts'
import { useRouter } from 'vue-router'
import {
    ROUTE_ENGAGEMENT_RING,
    ROUTE_ENGAGEMENT_RING_GEMSTONE,
    ROUTE_ENGAGEMENT_RING_LAB_GROWN,
    ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS,
    ROUTE_ENGAGEMENT_RINGS,
    ROUTE_NATURAL_FANCY_DIAMOND_SEARCH,
    ROUTE_GEMSTONE_SEARCH,
    ROUTE_LAB_GROWN_DIAMOND_SEARCH,
    ROUTE_NATURAL_DIAMOND_SEARCH,
    ROUTE_NON_SKU_GEMSTONE_SEARCH,
    ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH,
    ROUTE_LAB_GROWN_FANCY_DIAMOND_SEARCH,
    ROUTE_WEDDING_RINGS,
    ROUTE_WEDDING_RINGS_HOME,
    ROUTE_READY_TO_GO_RINGS,
    ROUTE_JEWELLERIES,
} from '@/router'
import {
    type RingRecommendedSizeOrSizeValue,
    useRingSize,
} from '@/domain/product/ring/useRingSize.ts'
import {
    type RingBuilderDataStore,
    useRingBuilderDataStore,
} from '@/plugins/ring-builder-data-store.ts'

import { type BenchFunction } from '@/plugins/bench.ts'
import type { OperationResult } from '@urql/core'

import md5 from 'md5'

export type SignatureStone =
    | CatalogueEngagementRingSignatureStoneInput
    | undefined
export type RingSize = RingRecommendedSizeOrSizeValue | null
export type StoneUri = string | null

type AddRingTypes =
    | CatalogueEngagementRingInputFragment
    | CatalogueEngagementRingFilterInput
type AddStoneTypes = RingBuilderStoneTypes | NonSkuParams

export interface RingBuilder extends RingBuilderActions {
    // Main items
    ring: Readonly<Ref<RingBuilderRingTypes | null>>
    stone: ComputedRef<RingBuilderStoneTypes | null>

    // Cookie-persisted store
    stoneUri: Readonly<Ref<StoneUri>>
    signatureStone: Ref<SignatureStone>

    lastAdded: Readonly<Ref<LastAddedTypename | null>>

    isValid: Readonly<Ref<boolean>>

    // Data Convenience
    productId: Readonly<Ref<string>>
    image: Readonly<
        Ref<
            | {
                  small_x2: MonolithImageSizeFragment
                  large_x2: MonolithImageSizeFragment
              }
            | undefined
        >
    >
    imageFor3rdStep: Readonly<
        Ref<
            | {
                  small_x2: MonolithImageSizeFragment
                  large_x2: MonolithImageSizeFragment
              }
            | undefined
            | null
        >
    >
    mountFragment: Readonly<Ref<RingBuilderMountFragmentFragment | null>>
    centreStoneFragment: Readonly<
        Ref<RingBuilderCentreStoneFragmentFragment | null>
    >

    // RB stone item
    stoneShapeAndWeight: Readonly<Ref<string>>
    stoneDescription: Readonly<Ref<string>>
    stonePrice: Readonly<Ref<string>>

    // Finance Options
    affirmFinanceOptions: Readonly<
        Ref<AffirmFinanceOptionsFragment | undefined | null>
    >
    klarnaFinanceOptions: Readonly<
        Ref<KlarnaFinanceOptionsFragment | undefined | null>
    >

    // Prices
    // todo those two ???
    ringTotalSaleAmount: Readonly<Ref<number>>
    ringTotalSaleAmountFormatted: Readonly<Ref<string>>

    // todo these 4 total sales are redundant as they are
    salePrice: Readonly<Ref<TaxSnippetFragment | null | undefined>>
    totalSale: Readonly<Ref<MoneyFragment>>
    totalSaleAmount: Readonly<Ref<number>>
    totalSaleAmountFormatted: Readonly<Ref<string>>

    loadPromise: Promise<void>
    markAsLoaded: () => void
}

interface RingBuilderDependencies {
    translator: AppTranslator
    currency: ComputedRef<CurrencyInput>
    regionPlugin: RegionPlugin
    formatMoney: MoneyFormatter
    routes: Routes
    routerNavigation: RouterNavigationPlugin
    ringBuilderDataStore: RingBuilderDataStore
    ringBuilderCookieStore: RingBuilderCookieStore
    ringBuilderSetMutation: SimpleMutationResponse<
        RingBuilderSetMutation,
        RingBuilderSetMutationVariables
    >
    currentUrl: ReadonlyRef<URL>
    bench: BenchFunction
}

type LastAddedTypename =
    | GraphQLTypeName<RingBuilderRingTypes>
    | GraphQLTypeName<RingBuilderStoneTypes>

const ringBuilderKey = 'app.plugin.ringBuilder.real.todo.rename.others'

export function installRingBuilder(
    app: App,
    dependencies: RingBuilderDependencies
): RingBuilder {
    const ringBuilder = createRingBuilder(dependencies)

    app.provide(ringBuilderKey, ringBuilder)

    return ringBuilder
}

export function useRingBuilder(): RingBuilder {
    return inject(ringBuilderKey) as RingBuilder
}

function createRingBuilder(dependencies: RingBuilderDependencies): RingBuilder {
    const {
        translator,
        currency,
        regionPlugin,
        formatMoney,
        routes,
        routerNavigation,
        ringBuilderDataStore,
        ringBuilderCookieStore,
        ringBuilderSetMutation,
        currentUrl,
        bench,
    } = dependencies

    const { state: ringBuilder } = ringBuilderDataStore
    const { stoneUri, signatureStone } = ringBuilderCookieStore

    const mountFragment = computed(() => getMount(ringBuilder.value))
    const centreStoneFragment = computed(() =>
        getCentreStone(ringBuilder.value)
    )

    const ring = toRef(() => mountFragment.value?.ring || null)

    const stone = computed(() => {
        const fragment = centreStoneFragment.value

        if (!fragment) {
            return null
        }

        return fragment.stone?.__typename === 'GemsApiStone'
            ? fragment.stone.stone
            : fragment.stone
    })

    const mountTotalSalePrice = toRef(
        () =>
            mountFragment.value?.totalSalePrice || {
                __typename: 'Money',
                currency: 'GBP',
                amount: '0',
            }
    )

    const centreStoneTotalSalePrice = computed(
        () =>
            centreStoneFragment.value?.totalSalePrice ||
            ({
                __typename: 'Money',
                currency: 'GBP',
                amount: '0',
            } as const)
    )
    const stoneTotalSaleAmountFormatted = computed(() =>
        formatMoney(centreStoneTotalSalePrice.value)
    )

    const stoneShapeAndWeight = computed(
        () => `${getStoneShape(stone.value)} ${getStoneWeight(stone.value)}`
    )

    const stoneCertificate = toCertificate(
        computed(() => {
            if (stone.value?.__typename === 'GemsApi_Stone') {
                return stone.value
            }

            return null
        })
    )

    const stoneCertificateDescription = computed(() => {
        if (!stoneCertificate.value) {
            return ''
        }

        return `, ${stoneCertificate.value.lab} Cert`
    })

    const stoneDescription = computed(
        () =>
            `${getStoneCategory(stone.value)}, ${getStoneColorAndClarity(
                stone.value
            )}${stoneCertificateDescription.value}`
    )

    const lastAdded = computed((): LastAddedTypename | null => {
        const lastAdded =
            ringBuilder.value?.position2 || ringBuilder.value?.position1

        if (!lastAdded) {
            return null
        }

        switch (lastAdded?.__typename) {
            case 'RingBuilderMountFragment':
                return 'CatalogueEngagementRing'
            case 'RingBuilderCentreStoneFragment':
                return lastAdded.stone?.__typename === 'GemsApiStone'
                    ? 'GemsApi_Stone'
                    : lastAdded.stone?.__typename || null
            default:
                return assertNever(lastAdded, null)
        }
    })

    const isValid = toRef(() => !ringBuilder.value?.failure?.errors.length)

    const klarnaFinanceOptions = toRef(
        () => ringBuilder.value?.financeOptionsKlarna
    )
    const affirmFinanceOptions = toRef(
        () => ringBuilder.value?.financeOptionsAffirm
    )

    // Prices TODO: Why do we need so many of them
    const ringTotalSaleAmount = computed(() =>
        parseFloat(mountTotalSalePrice.value.amount)
    )
    const ringTotalSaleAmountFormatted = computed(() =>
        formatMoney(mountTotalSalePrice.value)
    )

    const totalSaleMoney = computed(
        () =>
            ringBuilder.value?.salePrice.total ||
            ({
                __typename: 'Money',
                amount: '0',
                currency: 'GBP',
            } as const)
    )
    const totalSaleAmount = computed(() =>
        parseFloat(totalSaleMoney.value.amount)
    )
    const totalSaleAmountFormatted = computed(() =>
        totalSaleAmount.value ? formatMoney(totalSaleMoney.value) : ''
    )

    const {
        fetchingRingBuilderSetMutation,
        addRing,
        removeRing,
        addStone,
        removeStone,
        addRingAndStone,
    } = createRingBuilderActions({
        ringBuilderDataStore,
        ringBuilderCookieStore,
        ringBuilderSetMutation,
        routes,
        routerNavigation,
        translator,
        currency,
        regionPlugin,
        bench,
        ring,
        currentUrl,
    })

    const { promise: loadPromise, resolve: markAsLoaded } =
        Promise.withResolvers<void>()

    return {
        // Main items
        ring,
        stone,

        // Cookie-persisted store
        stoneUri,
        signatureStone,

        lastAdded,

        isValid,
        fetchingRingBuilderSetMutation,

        // Actions
        addRing,
        removeRing,
        addStone,
        removeStone,
        addRingAndStone,

        // Data Convenience
        productId: toRef(ringBuilder.value?.productId ?? ''),
        image: computed(
            () =>
                ringBuilder.value?.image || mountFragment.value?.ring?.images[0]
        ),
        imageFor3rdStep: computed(() => ringBuilder.value?.image),
        mountFragment,
        centreStoneFragment,

        // Random Strings
        stoneShapeAndWeight,
        stoneDescription,
        stonePrice: stoneTotalSaleAmountFormatted,

        // Finance Options
        klarnaFinanceOptions,
        affirmFinanceOptions,

        // Prices TODO: Why do we need so many of them
        ringTotalSaleAmount,
        ringTotalSaleAmountFormatted,

        salePrice: toRef(() => ringBuilder.value?.salePrice),
        totalSale: totalSaleMoney,
        totalSaleAmount,
        totalSaleAmountFormatted,

        loadPromise,
        markAsLoaded,
    }
}

interface RingBuilderActions {
    fetchingRingBuilderSetMutation: ReadonlyRef<boolean>

    // Actions
    addRing(
        r: AddRingTypes,
        signatureStone?: CatalogueEngagementRingSignatureStoneInput
    ): Promise<void>
    removeRing(): Promise<void>
    addStone(r: AddStoneTypes): Promise<void>
    removeStone(): Promise<void>
    addRingAndStone(
        r: AddRingTypes,
        s: AddStoneTypes,
        signatureStone?: CatalogueEngagementRingSignatureStoneInput
    ): Promise<void>
}

function createRingBuilderActions(dependencies: {
    ringBuilderDataStore: RingBuilderDataStore
    ringBuilderCookieStore: RingBuilderCookieStore
    ringBuilderSetMutation: SimpleMutationResponse<
        RingBuilderSetMutation,
        RingBuilderSetMutationVariables
    >
    routes: Routes
    routerNavigation: RouterNavigationPlugin
    translator: AppTranslator
    currency: ComputedRef<CurrencyInput>
    regionPlugin: RegionPlugin
    bench: BenchFunction
    ring: Readonly<Ref<RingBuilderRingTypes | null>>
    currentUrl: ReadonlyRef<URL>
}): RingBuilderActions {
    const {
        ringBuilderDataStore,
        ringBuilderCookieStore,
        ringBuilderSetMutation,
        routes,
        routerNavigation,
        translator,
        currency,
        regionPlugin,
        bench,
        ring,
        currentUrl,
    } = dependencies

    const { routerPush } = routerNavigation
    const { setState } = ringBuilderDataStore
    const { locale } = translator
    const { regionCode } = regionPlugin

    const { executeMutation, fetching: fetchingRingBuilderSetMutation } =
        ringBuilderSetMutation

    let hash: string | null = null
    let promise: Promise<OperationResult<RingBuilderSetMutation>> | null = null

    const executeRingBuilderSetMutation = async (
        variables: RingBuilderSetMutationVariables
    ): Promise<OperationResult<RingBuilderSetMutation>> => {
        const currentHash = md5(JSON.stringify(variables))

        if (hash && promise) {
            if (hash === currentHash) {
                // Await the current promise as it's the same variables
                return promise
            }

            // Variables are different from the currently running promise vars
            // Await the previous call first
            await promise
            // Then do another call with the new variables
            // This aims to avoid the current call being too quick
            // And the previous being too slow
            // From overwriting the state with its (would be stale) data
        }

        hash = currentHash
        const ben = bench(`Mutation RingBuilderSetMutation`)
        promise = executeMutation(variables).then((r) => {
            ben.end('END')
            setState(r.data?.ringBuilderSet.ringBuilder)
            hash = null
            promise = null

            return r
        })

        return promise
    }

    const addRing: RingBuilder['addRing'] = async (r, signatureStone) => {
        await executeRingBuilderSetMutation({
            currency: currency.value,
            locale: locale.value,
            region: regionCode.value,
            input: {
                mount: {
                    engagementRing:
                        '__typename' in r
                            ? getCatalogueEngagementRingFilterInput(r)
                            : r,
                    signatureStone,
                    uri: currentUrl.value.href,
                },
            },
        })
    }

    const removeRing: RingBuilder['removeRing'] = async () => {
        if (!ring.value) {
            return
        }

        routerPush(
            routes.engagementRings({
                shape: ring.value.centreStoneConstraints.shape.systemCode,
            })
        )

        await executeRingBuilderSetMutation({
            currency: currency.value,
            locale: locale.value,
            region: regionCode.value,
            input: { mount: {} },
        }).then((r) => {
            setState(r.data?.ringBuilderSet.ringBuilder)
        })
    }

    const { stoneUri } = ringBuilderCookieStore

    const addStone: RingBuilder['addStone'] = async (s) => {
        stoneUri.value = currentUrl.value.href

        await executeRingBuilderSetMutation({
            currency: currency.value,
            locale: locale.value,
            region: regionCode.value,
            input: {
                centreStone: stoneToRingBuilderInput(s, currentUrl.value.href),
            },
        })
    }

    const addRingAndStone: RingBuilder['addRingAndStone'] = async (
        r,
        s,
        signatureStone
    ) => {
        stoneUri.value = currentUrl.value.href

        await executeRingBuilderSetMutation({
            currency: currency.value,
            locale: locale.value,
            region: regionCode.value,
            input: {
                mount: {
                    engagementRing:
                        '__typename' in r
                            ? getCatalogueEngagementRingFilterInput(r)
                            : r,
                    signatureStone,
                    uri: currentUrl.value.href,
                },
                centreStone: stoneToRingBuilderInput(s, currentUrl.value.href),
            },
        })
    }

    const removeStone: RingBuilder['removeStone'] = async () => {
        if (ring.value) {
            routerPush(routes.ringBuilder.ring(ring.value, null))
        }

        stoneUri.value = null

        await executeRingBuilderSetMutation({
            currency: currency.value,
            locale: locale.value,
            region: regionCode.value,
            input: { centreStone: {} },
        })
    }

    return {
        fetchingRingBuilderSetMutation,

        // Actions
        addRing,
        removeRing,
        addStone,
        removeStone,
        addRingAndStone,
    }
}

function stoneToRingBuilderInput(
    stone: AddStoneTypes,
    currentUrl: string
): RingBuilderCentreStoneFragmentInput | undefined {
    if ('__typename' in stone) {
        switch (stone.__typename) {
            case 'GemsApi_Stone':
                return {
                    gemsApiIdentifier: gemsApiStoneToInput(stone),
                    uri: currentUrl,
                }
            case 'NonSkuLabGrown':
                return {
                    nonSkuLabGrownDiamond:
                        nonSkuLabGrownDiamondToFilterInput(stone),
                    uri: currentUrl,
                }
            case 'NonSkuGemstone':
                return {
                    nonSkuGemstone: nonSkuLabGemstoneToFilterInput(stone),
                    uri: currentUrl,
                }
            default:
                return assertNever(stone, undefined)
        }
    } else {
        switch (stone.type) {
            case 'gemstone':
                return {
                    nonSkuGemstone: stone.params.nonSkuGemStoneFilter,
                    uri: currentUrl,
                }
            case 'lab-grown':
                return {
                    nonSkuLabGrownDiamond: stone.params.nonSkuLabGrownFilter,
                    uri: currentUrl,
                }
            default:
                return assertNever(stone, undefined)
        }
    }
}

export function getMount(
    rb: RbFragment | null | undefined
): RingBuilderMountFragmentFragment | null {
    return !rb
        ? null
        : getMountFromPosition(rb.position1) ||
              getMountFromPosition(rb.position2)
}

function getMountFromPosition(
    position: RingBuilderFragmentFragment | null
): RingBuilderMountFragmentFragment | null {
    return position?.__typename !== 'RingBuilderMountFragment' ? null : position
}

function getCentreStoneFromPosition(
    position: RingBuilderFragmentFragment | null
): RingBuilderCentreStoneFragmentFragment | null {
    return position?.__typename !== 'RingBuilderCentreStoneFragment'
        ? null
        : position
}

export function getCentreStone(
    rb: RbFragment | null | undefined
): RingBuilderCentreStoneFragmentFragment | null {
    if (!rb) {
        return null
    }

    return (
        getCentreStoneFromPosition(rb.position1) ||
        getCentreStoneFromPosition(rb.position2)
    )
}

export function useEngagementRingSize(): ReturnType<typeof useRingSize> {
    const { ring } = useRingBuilder()
    const { ringSizePersisted } = useRingBuilderStore()

    return useRingSize(
        toRef(() => ring.value?.sizes),
        ringSizePersisted
    )
}

export function useImageFor3rdStep(): Readonly<
    Ref<
        | {
              small_x2: MonolithImageSizeFragment
              large_x2: MonolithImageSizeFragment
          }
        | undefined
        | null
    >
> {
    const ringBuilder = useRingBuilderDataStore()

    return computed(() => ringBuilder.state.value?.image)
}

export function useStoneShapeAndWeight(): Readonly<Ref<string>> {
    const { stone } = useRingBuilder()

    return computed(
        () => `${getStoneShape(stone.value)} ${getStoneWeight(stone.value)}`
    )
}

export function useIsRingBuilderActive(): ComputedRef<boolean> {
    const router = useRouter()
    const { ring, stone } = useRingBuilder()

    const isERFlow = computed(() => {
        switch (router.currentRoute.value.name) {
            case ROUTE_ENGAGEMENT_RING:
            // fallthrough
            case ROUTE_ENGAGEMENT_RINGS:
            // fallthrough
            case ROUTE_ENGAGEMENT_RING_GEMSTONE:
            // fallthrough
            case ROUTE_ENGAGEMENT_RING_LAB_GROWN:
            // fallthrough
            case ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS:
            // fallthrough
            case ROUTE_NATURAL_DIAMOND_SEARCH:
            // fallthrough
            case ROUTE_LAB_GROWN_DIAMOND_SEARCH:
            // fallthrough
            case ROUTE_NATURAL_FANCY_DIAMOND_SEARCH:
            // fallthrough
            case ROUTE_LAB_GROWN_FANCY_DIAMOND_SEARCH:
            // fallthrough
            case ROUTE_GEMSTONE_SEARCH:
            // fallthrough
            case ROUTE_NON_SKU_GEMSTONE_SEARCH:
            // fallthrough
            case ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH:
                return true
            default:
                return false
        }
    })

    // Hide on non ER PLPs
    const isNonErPlp = computed(() => {
        switch (router.currentRoute.value.name) {
            case ROUTE_WEDDING_RINGS:
            // fallthrough
            case ROUTE_WEDDING_RINGS_HOME:
            // fallthrough
            case ROUTE_READY_TO_GO_RINGS:
            // fallthrough
            case ROUTE_JEWELLERIES:
                return true
            default:
                return false
        }
    })

    return computed(
        () =>
            !isNonErPlp.value &&
            (Boolean(ring.value || stone.value) || isERFlow.value)
    )
}

export function useRingBuilderRingShape(): ComputedRef<
    RingBuilderCentreStoneShapeConstraint | undefined
> {
    const { ring } = useRingBuilder()

    return computed(() => ring.value?.centreStoneConstraints?.shape)
}

export function useRingBuilderStoneTypeLabel(): ComputedRef<string> {
    const { stone } = useRingBuilder()

    // TODO Translate
    return computed(() => {
        const stoneValue = stone.value

        if (!stoneValue) {
            return 'Diamond'
        }

        switch (stoneValue?.__typename) {
            case 'GemsApi_Stone':
                if (!stoneValue.categoryWithInfo.info) {
                    return 'Diamond'
                }

                return getGemsApiCategoryLabel(
                    stoneValue.categoryWithInfo.info?.systemCode
                )
            case 'NonSkuLabGrown':
                return 'Diamond'
            case 'NonSkuGemstone':
                return 'Gemstone'
            default:
                return assertNever(stoneValue, 'Diamond')
        }
    })
}

function getGemsApiCategoryLabel(category: GemsApi_StoneCategory): string {
    // TODO Translate
    switch (category) {
        case 'DIAMOND':
        case 'LAB_GROWN_DIAMOND':
            return 'Diamond'
        case 'GEMSTONE':
            return 'Gemstone'
        case 'FANCY_DIAMOND':
        case 'LAB_GROWN_FANCY_DIAMOND':
            return 'Coloured Diamond'
    }
}
